import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { StatusBadge } from "@/components/StatusBadge";
import {
    MapPin,
    Phone,
    Mail,
    Globe,
    Instagram,
    Calendar,
    Clock,
    DollarSign,
    GraduationCap,
    Sparkles,
    LinkIcon,
    AlertTriangle,
} from "lucide-react";

export function ClassDetailModal({ row, onClose }) {
    if (!row) return null;
    const Row = ({ icon: Icon, label, value }) =>
        value ? (
            <div className="flex items-start gap-4 py-4 border-b border-border last:border-b-0">
                <Icon className="h-4 w-4 mt-1 text-primary shrink-0" />
                <div className="flex-1">
                    <div className="font-mono-label text-[10px] text-muted-foreground mb-1">
                        {label}
                    </div>
                    <div className="text-sm text-foreground/90 break-words">{value}</div>
                </div>
            </div>
        ) : null;

    return (
        <Dialog open={!!row} onOpenChange={(o) => !o && onClose()}>
            <DialogContent
                className="rounded-none max-w-3xl w-[95vw] max-h-[92vh] overflow-y-auto border-foreground bg-background p-0"
                data-testid="class-detail-modal"
            >
                <DialogHeader className="p-6 md:p-10 pb-0 text-left">
                    <div className="flex items-center gap-2 mb-4">
                        <span className="font-mono-label text-[10px] text-muted-foreground">
                            {row.category}
                        </span>
                        <span className="text-muted-foreground">/</span>
                        <span className="font-mono-label text-[10px] text-muted-foreground">
                            {row.borough} · {row.neighborhood}
                        </span>
                        {row.manual_review && (
                            <Badge className="rounded-none bg-[#FFCC00] text-foreground hover:bg-[#FFCC00] ml-auto">
                                <AlertTriangle className="h-3 w-3 mr-1" /> Needs manual review
                            </Badge>
                        )}
                    </div>
                    <DialogTitle className="font-display text-3xl md:text-5xl leading-[1]">
                        {row.class_name}
                    </DialogTitle>
                    <DialogDescription className="font-editorial text-lg text-muted-foreground mt-2">
                        at {row.studio_name}
                    </DialogDescription>
                    {row.class_status && (
                        <div className="mt-4">
                            <StatusBadge status={row.class_status} />
                        </div>
                    )}
                </DialogHeader>

                <div className="p-6 md:p-10 pt-6">
                    <p className="text-base leading-relaxed text-foreground/90 mb-8">
                        {row.description}
                    </p>

                    <div className="grid md:grid-cols-2 gap-x-12">
                        <div>
                            <Row icon={MapPin} label="Address" value={`${row.address}, ${row.city}, ${row.state} ${row.zip_code}`} />
                            <Row icon={Calendar} label="Schedule" value={row.schedule} />
                            <Row icon={Clock} label="Class duration" value={row.duration_minutes ? `${row.duration_minutes} minutes` : ""} />
                            <Row icon={DollarSign} label="Price per class" value={row.price_per_class} />
                            <Row icon={DollarSign} label="Membership / packages" value={row.membership_pricing} />
                            <Row icon={Sparkles} label="Intro offer" value={row.intro_offer} />
                        </div>
                        <div>
                            <Row icon={GraduationCap} label="Skill level" value={row.skill_level} />
                            <Row icon={Sparkles} label="Audience focus" value={row.women_focus} />
                            <Row icon={Sparkles} label="Amenities" value={row.amenities} />
                            <Row icon={Phone} label="Phone" value={row.phone} />
                            <Row icon={Mail} label="Email" value={row.email} />
                            <Row
                                icon={Globe}
                                label="Website"
                                value={
                                    row.website_url ? (
                                        <a
                                            href={row.website_url}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="underline decoration-primary underline-offset-4"
                                        >
                                            {row.website_url}
                                        </a>
                                    ) : null
                                }
                            />
                            <Row
                                icon={Instagram}
                                label="Instagram"
                                value={
                                    row.instagram ? (
                                        <a
                                            href={row.instagram}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="underline decoration-primary underline-offset-4"
                                        >
                                            {row.instagram.replace("https://instagram.com/", "@")}
                                        </a>
                                    ) : null
                                }
                            />
                            <Row
                                icon={LinkIcon}
                                label="Source"
                                value={
                                    row.source_url ? (
                                        <a
                                            href={row.source_url}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="underline decoration-primary underline-offset-4 break-all"
                                        >
                                            {row.source_url}
                                        </a>
                                    ) : null
                                }
                            />
                        </div>
                    </div>

                    <div className="mt-10 flex flex-wrap gap-3">
                        {row.booking_url && (
                            <a
                                href={row.booking_url}
                                target="_blank"
                                rel="noreferrer"
                                className="bg-foreground text-background uppercase tracking-widest text-xs font-bold px-6 py-4 hover-invert border border-foreground"
                                data-testid="book-now-btn"
                            >
                                Book on Studio Site →
                            </a>
                        )}
                        {row.website_url && (
                            <a
                                href={row.website_url}
                                target="_blank"
                                rel="noreferrer"
                                className="bg-background text-foreground uppercase tracking-widest text-xs font-bold px-6 py-4 hover-invert border border-foreground"
                                data-testid="visit-studio-btn"
                            >
                                Visit Studio
                            </a>
                        )}
                    </div>

                    <p className="mt-8 font-mono-label text-[10px] text-muted-foreground">
                        Last verified · {row.last_verified}
                    </p>

                    <div className="mt-6 pt-6 border-t border-border flex items-center justify-between gap-3">
                        <p className="text-xs text-muted-foreground">
                            Are you the owner of <strong>{row.studio_name}</strong>?
                        </p>
                        <a
                            href={`/admin`}
                            onClick={(e) => {
                                e.preventDefault();
                                window.dispatchEvent(
                                    new CustomEvent("claim:open", { detail: row })
                                );
                            }}
                            className="font-mono-label text-[10px] uppercase tracking-widest hover-invert px-3 py-2 border border-foreground"
                            data-testid="claim-from-detail-btn"
                        >
                            Claim & update →
                        </a>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
